import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  Zap,
  ArrowLeftRight,
  BarChart3,
  Home,
  BookOpen,
  Code,
  FileText,
  ChevronDown,
  ExternalLink,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./V1Layout.css";

function V1Layout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [apiDocsExpanded, setApiDocsExpanded] = useState(false);

  // Get API Docs URLs for XOrbit endpoints
  const getApiDocsUrl = (version) => {
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:5001'
      : `${window.location.protocol}//${window.location.hostname}`;
    // V3 requires admin user ID for access, V4 is public
    if (version === 'v3') {
      const userId = user?._id || user?.id || '';
      return `${baseUrl}/api/extraction/xorbit/${version}/docs?uid=${encodeURIComponent(userId)}`;
    }
    // V4 and others don't need uid
    return `${baseUrl}/api/extraction/xorbit/${version}/docs`;
  };

  // Build API docs items - V3 only visible to admin/superadmin, V4 visible to all
  const apiDocsItems = [
    { url: getApiDocsUrl('v1'), label: 'XOrbit v1', description: 'Gemini', color: '#4285f4', className: 'gemini' },
    { url: getApiDocsUrl('v2'), label: 'XOrbit v2', description: 'Qwen', color: '#ff9900', className: 'qwen' },
    // V3 is restricted to admin/superadmin users only (internal use)
    ...(user?.role === 'admin' || user?.role === 'superadmin' 
      ? [{ url: getApiDocsUrl('v3'), label: 'XOrbit v3', description: 'Preprocessing + Qwen', color: '#10b981', className: 'preprocessing' }]
      : []),
    // V4 is accessible to all users (Textract + Qwen)
    { url: getApiDocsUrl('v4'), label: 'XOrbit v4', description: 'Textract + Qwen', color: '#059669', className: 'textract' },
  ];

  const menuItems = [
    { path: "/v1", icon: Home, label: "Dashboard", exact: true },
    { path: "/v1/xorbit", icon: Zap, label: "XOrbit", color: "#8b5cf6", description: "Gemini 3.5 Flash" },
    { path: "/v1/vja", icon: Zap, label: "VJA", color: "#0ea5e9", description: "Qwen VL" },
    { path: "/v1/comparison", icon: ArrowLeftRight, label: "Comparison", color: "#6366f1" },
    // V3 Work Orders - visible only to admin/superadmin
    ...(user?.role === 'admin' || user?.role === 'superadmin' 
      ? [{ path: "/v1/v3-workorders", icon: Zap, label: "V3 Work Orders", color: "#10b981", description: "Preprocessing + Qwen" }]
      : []),
    // V4 Work Orders - visible to all users (Textract + Qwen)
    { path: "/v1/v4-workorders", icon: Zap, label: "V4 Work Orders", color: "#059669", description: "Textract + Qwen" },
    { path: "/v1/stats", icon: BarChart3, label: "Statistics" },
    { path: "/v1/api-logs", icon: Code, label: "API Logs" },
    { path: "/v1/portal-logs", icon: FileText, label: "Portal Logs" },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    onLogout();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/v1" || location.pathname === "/v1/") {
      return "V1 Dashboard";
    }
    if (location.pathname.startsWith("/v1/work-order/")) {
      return "Work Order Details";
    }
    const item = menuItems.find(m => isActive(m) && !m.exact);
    if (item) {
      return item.description ? `${item.label} - ${item.description}` : item.label;
    }
    return "V1 Pipeline";
  };

  return (
    <div className="v1-layout">
      {/* Sidebar */}
      <aside className="v1-sidebar">
        <div className="v1-sidebar-header">
        {(user?.pipeline === "all" || user?.role === "superadmin" || user?.role === "admin") && (
          <button onClick={() => navigate("/dashboard")} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",margin:"0 12px 8px",background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:6,cursor:"pointer",fontSize:12,color:"#475569",fontWeight:500}} title="Back to Main Dashboard">
            <span style={{fontSize:14}}>←</span> Main Dashboard
          </button>
        )}
          <div className="v1-logo">
            <Zap size={24} className="v1-logo-icon" />
            <span className="v1-logo-text">V1 Pipeline</span>
          </div>
          <div className="v1-logo-subtitle">XOrbit / VJA Models</div>
        </div>

        <nav className="v1-sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              className={`v1-nav-item ${isActive(item) ? "active" : ""}`}
              onClick={() => navigate(item.path)}
              title={item.label}
              style={item.color ? { "--item-color": item.color } : {}}
            >
              <item.icon size={20} style={item.color ? { color: item.color } : {}} />
              <div className="v1-nav-item-content">
                <span className="v1-nav-label">{item.label}</span>
                {item.description && <span className="v1-nav-description">{item.description}</span>}
              </div>
            </button>
          ))}

          {/* API Docs with Expandable Submenu */}
          <div className="v1-nav-submenu-wrapper">
            <button
              className={`v1-nav-item v1-nav-item-external ${apiDocsExpanded ? 'expanded' : ''}`}
              onClick={() => setApiDocsExpanded(!apiDocsExpanded)}
              title="API Docs"
            >
              <BookOpen size={20} />
              <div className="v1-nav-item-content">
                <span className="v1-nav-label">API Docs</span>
                <span className="v1-nav-description">Swagger UI</span>
              </div>
              <ChevronDown size={16} className={`v1-nav-chevron ${apiDocsExpanded ? 'rotated' : ''}`} />
            </button>

            {/* Submenu Items */}
            <div className={`v1-nav-submenu ${apiDocsExpanded ? 'expanded' : ''}`}>
              {apiDocsItems.map((doc) => (
                <button
                  key={doc.url}
                  className={`v1-nav-submenu-item ${doc.className}`}
                  onClick={() => window.open(doc.url, "_blank")}
                  style={{ '--item-accent': doc.color }}
                >
                  <span className="v1-submenu-dot" style={{ background: doc.color }}></span>
                  <div className="v1-submenu-content">
                    <span className="v1-submenu-label">{doc.label}</span>
                    <span className="v1-submenu-desc">{doc.description}</span>
                  </div>
                  <ExternalLink size={14} className="v1-submenu-external-icon" />
                </button>
              ))}
            </div>
          </div>
        </nav>

        <div className="v1-sidebar-footer">
          <button
            className="v1-nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <div className="v1-nav-item-content">
              <span className="v1-nav-label">Logout</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="v1-layout-content">
        {/* Top Header */}
        <header className="v1-top-header">
          <div className="v1-header-title">{getPageTitle()}</div>

          <div className="v1-header-actions">
            <NotificationsPanel />
            
            <div className="v1-user-menu">
              <button
                className="v1-user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="v1-user-avatar">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
                <span className="v1-user-name">{user?.name}</span>
              </button>

              {showUserMenu && (
                <div className="v1-user-dropdown">
                  <div className="v1-dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="v1-dropdown-name">{user?.name}</div>
                      <div className="v1-dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="v1-dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="v1-page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default V1Layout;
